import type { FieldProps } from 'formik';
import { Field, getIn } from 'formik';
import React from 'react';
import type { OptionTypeBase } from '../Select';
import type { FormFieldProps } from './FormField';
import FormField from './FormField';

export const getFormikProps = <
  Option extends OptionTypeBase,
  IsMulti extends boolean,
  IsClearable extends boolean,
  P extends FormFieldProps<Option, IsMulti, IsClearable>,
>(
  props: P,
  {
    field,
    form: { errors, setFieldTouched, setFieldValue, touched },
  }: FieldProps,
): P => {
  const { name } = field;
  // using `getIn` for FieldArray fields
  const error = getIn(touched, name) ? getIn(errors, name) : null;

  const baseProps = {
    ...field,
    ...props,
    error,
  };

  if (baseProps.type === 'select') {
    return {
      ...baseProps,
      type: baseProps.type,
      onBlur: () => {
        setFieldTouched(field.name, true);
      },
      onChange: (value, action) => {
        setFieldValue(field.name, value);
        if (baseProps.onChange) {
          baseProps.onChange(value, action);
        }
      },
    };
  }

  if (baseProps.type === 'textarea') {
    return {
      ...baseProps,
      type: baseProps.type,
      onChange: (value) => {
        field.onChange(value);
        if (baseProps.onChange) {
          baseProps.onChange(value);
        }
      },
    };
  }

  return {
    ...baseProps,
    onChange: (value) => {
      field.onChange(value);
      if (baseProps.onChange) {
        baseProps.onChange(value);
      }
    },
  };
};

const FormikFormField = <
  Option extends OptionTypeBase = Record<string, unknown>,
  IsMulti extends boolean = false,
  IsClearable extends boolean = false,
>(
  props: FormFieldProps<Option, IsMulti, IsClearable>,
) => (
  <Field {...props}>
    {(formikProps: FieldProps) => (
      <FormField<Option, IsMulti, IsClearable>
        {...getFormikProps<
          Option,
          IsMulti,
          IsClearable,
          FormFieldProps<Option, IsMulti, IsClearable>
        >(props, formikProps)}
        component={props.component}
      />
    )}
  </Field>
);

export default FormikFormField;
